import { Box } from '@mui/material';
import { MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { ProductMedia } from '../../../../../../ProductTypes';
import { slideHorizontally } from '../../../../utils/galleryMotion.utils';
import { getFocusZoomPercentToScroll } from '../../../../utils/getFocusZoomPercentToScroll';
import GalleryMedia from '../../../GalleryMedia/GalleryMedia';
import ProductGalleryNavButtons from '../../../ProductGalleryNavButtons/ProductGalleryNavButtons';
import { SQUARE_STACK_MEDIAS, getSquareStackPage } from '../../squareGallery.utils';
import { useSquareGalleryStackStyles } from './squareGalleryStack.styles';

interface SquareGalleryStackProps {
  medias: ProductMedia[];
  onZoom: (media: ProductMedia, focusPercentToScroll?: number) => void;
}

const SquareGalleryStack = ({ medias, onZoom }: SquareGalleryStackProps) => {
  const { classes } = useSquareGalleryStackStyles();
  const stackRef = useRef<HTMLDivElement | null>(null);
  const [slide, setSlide] = useState({ page: 0, fromOffset: 0 });

  const pagesCount = Math.max(1, Math.ceil(medias.length / SQUARE_STACK_MEDIAS));
  const pageMedias = getSquareStackPage({ medias, page: slide.page });
  const hasNavigation = pagesCount > 1;

  useLayoutEffect(() => {
    const stack = stackRef.current;

    if (!stack || !slide.fromOffset) {
      return;
    }

    const animation = slideHorizontally(stack, slide.fromOffset);

    return () => animation?.cancel();
  }, [slide]);

  const navigate = (direction: 1 | -1) => {
    const stackWidth = stackRef.current?.clientWidth ?? 0;

    setSlide((currentSlide) => ({
      page: (currentSlide.page + direction + pagesCount) % pagesCount,
      fromOffset: direction * stackWidth,
    }));
  };

  const handleMediaClick = (media: ProductMedia, event: MouseEvent<HTMLDivElement>) => {
    if (media.type !== 'IMAGE') {
      return;
    }

    onZoom(media, getFocusZoomPercentToScroll(event));
  };

  return (
    <Box ref={stackRef} className={classes.root}>
      {pageMedias.map((media) => (
        <Box
          key={media.id ?? media.url}
          className={classes.media}
          onClick={(event) => handleMediaClick(media, event)}
        >
          <GalleryMedia media={media} />
        </Box>
      ))}

      {hasNavigation && (
        <ProductGalleryNavButtons onPrevious={() => navigate(-1)} onNext={() => navigate(1)} />
      )}
    </Box>
  );
};

export default SquareGalleryStack;
